// Direct import from the scheduler component to ensure it's included in the build
import {
  PublicWrkbeltWindowProp,
  VersionManifest,
} from '@wrkbelt/shared/utils-booking-flow-data-logic';
import { WrkbeltScheduler } from '@wrkbelt/ui-components';
import { createElement } from 'react';
import {
  DEFAULT_BRAND_COLORS,
  SchedulerCdnBundleEvents,
  SchedulerListenerEvents,
} from './constants';
import { mountSchedulerShadow } from './setup-mounts';
import { WrkbeltSchedulerProps } from './types';

/**
 * Helper to safely parse JSON attributes
 */
function parseJsonAttribute<T>(value: string | null | undefined): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    console.error('Wrkbelt Scheduler: Failed to parse attribute', e);
    return null;
  }
}

/**
 * Initialize the scheduler and expose global API
 */
export function initScheduler(
  scriptElement: HTMLScriptElement,
  manifest: VersionManifest
): void {
  try {
    // Extract data attributes from script tag
    const organizationId = scriptElement.dataset.organizationId;
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    // Parse other attributes with defaults
    const defaultBrandKit = parseJsonAttribute<
      WrkbeltSchedulerProps['defaultBrandKit']
    >(scriptElement.dataset.defaultBrandKit) || {
      colors: DEFAULT_BRAND_COLORS,
    };

    const schedulerConfig = parseJsonAttribute<
      WrkbeltSchedulerProps['schedulerConfig']
    >(scriptElement.dataset.schedulerConfig) || {
      target_click_elements: [],
      fallback_phone_number_screen: {
        phone_number: '',
      },
    };

    const { root, schedulerContainer, portalContainer } =
      mountSchedulerShadow();

    // Create props for the Scheduler component
    const schedulerProps: WrkbeltSchedulerProps = {
      organizationId,
      defaultBrandKit,
      schedulerConfig,
      portalContainer,
      onInitialized: () => {
        // Dispatch event when scheduler is initialized
        const event = new CustomEvent(SchedulerListenerEvents.INITIALIZED, {
          detail: {
            organizationId,
            version: manifest.version,
            commit: manifest.commit.substring(0, 7),
          },
        });
        document.dispatchEvent(event);
        console.log('Wrkbelt Scheduler: Initialization complete');
      },
      onError: (error) => {
        console.error('Wrkbelt Scheduler Error:', error);
        const event = new CustomEvent(SchedulerListenerEvents.ERROR, {
          detail: {
            error,
            organizationId,
            version: manifest.version,
          },
        });
        document.dispatchEvent(event);
      },
    };

    // Render React component within shadow DOM
    // Ensure we're mounting to the correct container for proper isolation
    try {
      console.log('Wrkbelt Scheduler: Creating React root in shadow DOM');
      root.render(createElement(WrkbeltScheduler, schedulerProps));
      console.log('Wrkbelt Scheduler: React component rendered successfully');
    } catch (renderError) {
      console.error(
        'Wrkbelt Scheduler: Failed to render React component',
        renderError
      );
      throw new Error(
        `Failed to render scheduler component: ${
          renderError instanceof Error
            ? renderError.message
            : String(renderError)
        }`
      );
    }

    // Event handlers for the public API
    // These handlers dispatch events into the shadow DOM that the React components can listen for
    // Using typed event system for consistency and reliability
    const openHandler = (): void => {
      try {
        console.log('Wrkbelt Scheduler: Attempting to open scheduler modal');
        const event = new CustomEvent(SchedulerListenerEvents.OPEN_MODAL, {
          bubbles: true, // Allow the event to bubble through the shadow DOM boundary
          composed: true, // Allow the event to cross the shadow DOM boundary
        });

        // Dispatch to the react container within shadow DOM - this is where React components attach listeners
        schedulerContainer.dispatchEvent(event);
        console.log('Wrkbelt Scheduler: Open event dispatched successfully');
      } catch (eventError) {
        console.error(
          'Wrkbelt Scheduler: Failed to dispatch open event',
          eventError
        );
      }
    };

    const closeHandler = (): void => {
      try {
        console.log('Wrkbelt Scheduler: Attempting to close scheduler modal');
        const event = new CustomEvent(SchedulerListenerEvents.CLOSE_MODAL, {
          bubbles: true,
          composed: true,
        });

        // Dispatch to the react container within shadow DOM
        schedulerContainer.dispatchEvent(event);
        console.log('Wrkbelt Scheduler: Close event dispatched successfully');
      } catch (eventError) {
        console.error(
          'Wrkbelt Scheduler: Failed to dispatch close event',
          eventError
        );
      }
    };

    // Expose API to global scope
    if (!window.Wrkbelt) {
      window.Wrkbelt = {} as PublicWrkbeltWindowProp;
    }

    // Create a richer public API
    window.Wrkbelt.Scheduler = {
      open: openHandler,
      close: closeHandler,
      version: manifest.version,
      buildTime: manifest.buildTime,
      commit: manifest.commit,
      environment: manifest.environment,
      entryPoint: manifest.entryPoint,
      // Add isReady flag to indicate successful initialization
      isReady: true,
    };
  } catch (error) {
    console.error('Wrkbelt Scheduler: Initialization failed', error);

    // Create an error state API to avoid null references
    if (!window.Wrkbelt) {
      window.Wrkbelt = {} as PublicWrkbeltWindowProp;
    }

    window.Wrkbelt.Scheduler = {
      // Provide no-op functions that log errors when called
      open: () => {
        console.error('Wrkbelt Scheduler: Cannot open - initialization failed');
        return;
      },
      close: () => {
        console.error(
          'Wrkbelt Scheduler: Cannot close - initialization failed'
        );
        return;
      },
      version: manifest?.version || 'unknown',
      buildTime: manifest?.buildTime || new Date().toISOString(),
      commit: manifest?.commit || 'unknown',
      environment: manifest?.environment || 'unknown',
      entryPoint: manifest?.entryPoint || 'unknown',
      isReady: false,
      error: error instanceof Error ? error.message : String(error),
    };

    // Fire error event that clients can listen for
    const event = new CustomEvent(SchedulerCdnBundleEvents.BUNDLE_ERROR, {
      detail: {
        error: error instanceof Error ? error.message : String(error),
        version: manifest?.version || 'unknown',
      },
    });
    document.dispatchEvent(event);
  }
}

export const prepSchedulerForLoading = () => {
  // Register the initializer in the global scope
  window.WrkbeltLoader = {
    initScheduler,
  };
};
